import type { DREHierarchicalLine, DREHierarchicalNode, DRELineType, DREParentPath } from "../../../types/dashboard";
import type { ExpandNodeParams } from "./index";

type DRERowItem = DREHierarchicalLine | DREHierarchicalNode;

const isNode = (item: DRERowItem): item is DREHierarchicalNode => {
  return "nodeType" in item;
};

const extractOriginalId = (compositeId: string | number): string => {
  const parts = compositeId.toString().split("_");
  return parts[0];
};

const buildParentPath = (item: DREHierarchicalNode, parentPath?: DREParentPath): DREParentPath => {
  const newPath: DREParentPath = { ...parentPath };
  const originalId = extractOriginalId(item.id);

  switch (item.nodeType) {
    case "CATEGORIA":
      newPath.categoryId = parseInt(originalId, 10) || undefined;
      break;
    case "FILIAL":
      newPath.branchId = parseInt(originalId, 10) || undefined;
      break;
    case "SEGMENTO":
      newPath.segmentId = parseInt(originalId, 10) || undefined;
      break;
    case "CLIENTE":
    case "FORNECEDOR":
      newPath.clientOrSupplierId = parseInt(originalId, 10) || undefined;
      break;
    case "VIAGEM":
      newPath.tripCode = originalId;
      break;
    case "VEICULO":
      newPath.vehicleId = originalId;
      break;
  }

  return newPath;
};

interface DRERowProps {
  item: DRERowItem;
  lineType: DRELineType;
  expandedItems: string[];
  loadingNodes: string[];
  onToggle: (id: string) => void;
  onExpandNode: (params: ExpandNodeParams) => Promise<void>;
  parentPath?: DREParentPath;
  isLast?: boolean;
  depth?: number;
}

const getIndentStyle = (level: number): React.CSSProperties => {
  const basePadding = 25;
  const paddingPerLevel = 25;
  const paddingLeft = basePadding + level * paddingPerLevel;
  return { paddingLeft: `${paddingLeft}px` };
};

export default function DRERow({
  item,
  lineType,
  expandedItems,
  loadingNodes,
  onToggle,
  onExpandNode,
  parentPath,
  isLast = false,
  depth = 0,
}: DRERowProps) {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(item.id);
  const isLoading = loadingNodes.includes(item.id);
  const indentStyle = getIndentStyle(depth);
  const isBold = item.label === "Lucro Operacional" || item.label === "Margem de Lucro";
  const isNegative = item.value < 0;
  const canFetchChildren = isNode(item) && item.expandable && (!item.children || item.children.length === 0);

  const handleToggle = async () => {
    if (isExpanded) {
      onToggle(item.id);
      return;
    }

    if (canFetchChildren && isNode(item)) {
      const originalId = extractOriginalId(item.id);
      await onExpandNode({
        nodeId: originalId,
        compositeId: item.id,
        nodeType: item.nodeType,
        lineType,
        parentPath,
      });
    }

    onToggle(item.id);
  };

  const childParentPath = isNode(item) ? buildParentPath(item, parentPath) : parentPath;

  return (
    <>
      <div
        key={item.id}
        className={`display-flex align-items-center justify-content-between gap-10 padding-y-10 bg-white border ${
          isLast ? "border-none" : "border-bottom-only"
        }`}
      >
        <div className="flex-1-1" style={indentStyle}>
          <span className={`${isBold ? "bold" : ""}`}>{item.label}</span>
        </div>

        <div className={`${isNegative ? "text-color-danger" : ""} ${isBold ? "bold" : ""}`}>{item.formattedValue}</div>

        {hasChildren || canFetchChildren ? (
          <span
            className={`cursor-pointer margin-left-10 ${isLoading ? "opacity-50" : ""}`}
            onClick={isLoading ? undefined : handleToggle}
          >
            {isLoading ? (
              <span className="rioglyph rioglyph-spinner spinning" />
            ) : (
              <span className={`rioglyph ${isExpanded ? "rioglyph-chevron-up" : "rioglyph-chevron-down"}`} />
            )}
          </span>
        ) : (
          <span className="margin-left-25" />
        )}
      </div>

      {hasChildren &&
        isExpanded &&
        item.children!.map((child, index, array) => (
          <DRERow
            item={child}
            key={child.id}
            lineType={lineType}
            onToggle={onToggle}
            expandedItems={expandedItems}
            loadingNodes={loadingNodes}
            onExpandNode={onExpandNode}
            parentPath={childParentPath}
            isLast={isLast && index === array.length - 1}
            depth={depth + 1}
          />
        ))}
    </>
  );
}
